import { EditOutlined } from "@ant-design/icons";
import { Button, Col, message, Row, Table, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { patchOrganization, useGetConfig } from "../../../api/v1";
import { useOrganizationSelection } from "../../../hooks/useOrganizationSelection";
import { SelectOption } from "../../../libs/types";
import { convertToSnakeCase, convertToTitleCase, findSelectOption, getSelectOptionValue } from "../../../libs/utils";
import { Popup } from "../../atoms/Popup";
import SectionHeader from "../../molecules/SectionHeader";

import { PaymentTypeForm } from "./PaymentTypeForm";
import {
  leaseTypeOptions,
  patternOptions,
  paymentModeOptions,
  paymentTypeOptions,
  usageTypeOptions,
} from "./selectOptions";
import { defaultColumns } from "./tableColumns";
import { PaymentTypeFormData, PaymentTypePayload, PaymentTypeRecord } from "./types";
import { usePaymentsTypes } from "./usePaymentsTypes";

const emptyFormValues: PaymentTypeFormData = {
  organization: null,
  description: null,
  usageType: null,
  vatInPercentage: 0,
  modeOfPayment: null,
  paymentType: null,
  defaultAmount: 0,
  leaseType: null,
  pattern: null,
  isUpfrontPayment: null,
  isWriteOff: null,
};

export const PaymentTypesModule = () => {
  const [modalMode, setModalMode] = useState<"add" | "edit" | null>(null);
  const [editingRecord, setEditingRecord] = useState<PaymentTypeRecord | null>(null);

  const { orgId, organizations, organizationOptions, changeSelectedOrgId } = useOrganizationSelection();
  const { paymentTypes, refetchPaymentTypes } = usePaymentsTypes();

  const methods = useForm<PaymentTypeFormData>();

  const watchedOrganization = useWatch({ name: "organization", control: methods.control });
  const watchedUsageType = useWatch({ name: "usageType", control: methods.control });

  const { config } = useGetConfig({
    params: {
      key: "masterPaymentTypeAttributes",
      orgId: watchedOrganization?.value,
      usageType: watchedUsageType?.value,
    },
    isEnabled: Boolean(watchedOrganization?.value && watchedUsageType?.value),
  });

  const descriptionOptions: SelectOption[] = useMemo(() => {
    return Object.entries(config?.[0]?.masterPaymentTypeAttributes ?? {}).map(([value, label]: [string, string]) => ({
      value,
      label,
    }));
  }, [config]);

  useEffect(() => {
    if (!Array.isArray(descriptionOptions) || descriptionOptions?.length === 0 || !editingRecord?.type) return;
    methods.setValue("description", findSelectOption(descriptionOptions, editingRecord.type) ?? null);
  }, [descriptionOptions, editingRecord?.type]);

  const columns: ColumnsType<PaymentTypeRecord> = [
    ...defaultColumns,
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_: unknown, record: PaymentTypeRecord) => (
        <Button type="link" key={`edit-btn-${record.uid}`} onClick={() => handleEdit(record)}>
          <EditOutlined />
        </Button>
      ),
    },
  ];

  const selectedOrganization = orgId ? (findSelectOption(organizationOptions, orgId) ?? null) : null;

  const resetPaymentForm = () => {
    setModalMode(null);
    setEditingRecord(null);
    methods.reset(emptyFormValues);
  };

  const handleAdd = () => {
    if (!selectedOrganization) {
      message.warning("Select an organization");
      return;
    }

    setEditingRecord(null);
    setModalMode("add");
    methods.reset({
      ...emptyFormValues,
      organization: selectedOrganization,
    });
  };

  const handleEdit = (record: PaymentTypeRecord) => {
    if (!selectedOrganization) {
      message.warning("Select an organization");
      return;
    }

    setEditingRecord(record);
    setModalMode("edit");
    methods.reset({
      organization: selectedOrganization,
      description: null,
      usageType: findSelectOption(usageTypeOptions, record.usageType) ?? null,
      vatInPercentage: record.vatInPercentage || 0,
      modeOfPayment: findSelectOption(paymentModeOptions, record.modeOfPayment) ?? null,
      paymentType: findSelectOption(paymentTypeOptions, record.paymentType) ?? null,
      defaultAmount: record.defaultAmount || 0,
      leaseType: findSelectOption(leaseTypeOptions, record.leaseType) ?? null,
      pattern: findSelectOption(patternOptions, record.pattern) ?? null,
      isUpfrontPayment: record.isUpfrontPayment === "yes",
      isWriteOff: record.isWriteOff === "yes",
    });
  };

  const handleCancel = () => resetPaymentForm();

  const handleSubmit = async (formData: PaymentTypeFormData) => {
    try {
      const { organization, isUpfrontPayment, isWriteOff, ...restFormData } = formData;
      const selectedOrgId = organization ? getSelectOptionValue(organization) : null;

      if (!selectedOrgId) {
        message.warning("Select an organization");
        return;
      }

      const { description, usageType, modeOfPayment, paymentType, leaseType, pattern } = restFormData;

      if (!description?.value || !usageType || !modeOfPayment || !paymentType || !leaseType || !pattern) {
        message.warning("Please fill in all required fields");
        return;
      }

      const record: PaymentTypeRecord = {
        uid: editingRecord?.uid ?? crypto.randomUUID(),
        description: convertToTitleCase(description.label),
        type: convertToSnakeCase(description.value),
        usageType: usageType.value,
        vatInPercentage: Number(restFormData.vatInPercentage),
        modeOfPayment: modeOfPayment.value,
        paymentType: paymentType.value,
        defaultAmount: Number(restFormData.defaultAmount),
        leaseType: leaseType.value,
        pattern: pattern.value,
        isUpfrontPayment: isUpfrontPayment ? "yes" : "no",
        isWriteOff: isWriteOff ? "yes" : "no",
      };

      const updatedList = editingRecord?.uid
        ? paymentTypes.map((p) => (p.uid === editingRecord.uid ? record : p))
        : [...paymentTypes, record];

      const payload: PaymentTypePayload[] = updatedList.map(
        ({ uid: _uid, propertyLabels: _propertyLabels, ...rest }) => rest
      );

      const response = await patchOrganization({
        orgId: selectedOrgId,
        data: { masterPaymentTypes: payload },
      });

      if (response?.success) {
        message.success("Updated successfully");
        resetPaymentForm();

        await refetchPaymentTypes();
      } else {
        message.error(response?.message ?? "Unable to update");
      }
    } catch {
      message.error("Something went wrong");
    }
  };

  const popupConfigs = {
    add: {
      title: <Typography.Title level={3}>Add Payment Type</Typography.Title>,
      content: (
        <PaymentTypeForm
          formType="add"
          methods={methods}
          onSubmit={handleSubmit}
          descriptionOptions={descriptionOptions}
        />
      ),
      width: 650,
    },
    edit: {
      title: <Typography.Title level={3}>Edit Payment Type</Typography.Title>,
      content: (
        <PaymentTypeForm
          formType="edit"
          methods={methods}
          onSubmit={handleSubmit}
          descriptionOptions={descriptionOptions}
        />
      ),
      width: 650,
    },
  } as const;

  const selectedModalMode = modalMode ? popupConfigs[modalMode] : null;

  return (
    <>
      <SectionHeader
        heading="Payment Types"
        addNewAction={handleAdd}
        organizationList={organizations}
        setOrgId={changeSelectedOrgId}
        orgFilterInitialValue={{ value: orgId }}
      />

      <Row gutter={20}>
        <Col span={24}>
          <Table
            rowKey={(record) => record.uid}
            className="custom-table org_table_wrap"
            columns={columns}
            dataSource={paymentTypes}
          />
        </Col>
      </Row>
      <Popup
        closable
        visibility={Boolean(modalMode)}
        title={selectedModalMode?.title}
        content={selectedModalMode?.content}
        width={selectedModalMode?.width}
        onCancel={handleCancel}
      />
    </>
  );
};
